import * as React from 'react';

import { PrimaryButton } from '@components/library/button/index';
import { useInputs } from './useInputs';
import { FormProps } from './Form.props';
import { SelectionValue } from '../inputs/Input.props';

import './base_styles.scss';

const defaultSubmitButtonText = bfTranslate('Submit');

const defaultFormProps: Partial<FormProps> = {
  submitButtonText: defaultSubmitButtonText
};

const Form = (props: FormProps): JSX.Element => {
  const fullProps: FormProps = {
    ...defaultFormProps,
    ...props
  };

  const { children, disabledByParent, onAsyncCustomValidation, onSubmit, submitButtonText } = fullProps;
  const [submitDisabled, setSubmitDisabled] = React.useState(false);
  const [submitAttempted, setSubmitAttempted] = React.useState(false);
  const [isValidating, setIsValidating] = React.useState(false);
  const { inputs, handleBlur, handleChange, handleFocus, setInputs } = useInputs();

  const isFormValid = React.useCallback((): boolean => {
    let isValid = true;
    if (inputs) {
      Object.keys(inputs).forEach((input) => {
        if (inputs && input && inputs[input].error) {
          isValid = false;
        }
      });
    }
    return isValid;
  }, [inputs]);


  const submitFailed = (): void => {
    setIsValidating(false);
    setSubmitDisabled(true);
    setSubmitAttempted(true);
  };

  const handleSubmit = async (event: React.SyntheticEvent): Promise<void> => {
    if (event) event.preventDefault();

    const shouldSubmit = isFormValid();

    if (inputs && onSubmit && shouldSubmit && !onAsyncCustomValidation) {
      onSubmit(inputs);
    } else if (!shouldSubmit) {
      submitFailed();
    } else if (onSubmit && inputs && onAsyncCustomValidation) {
      setIsValidating(true);
      const customErrorMapping = await onAsyncCustomValidation(inputs);
      if (customErrorMapping.length === 0) {
        onSubmit(inputs);
      } else {
        customErrorMapping.forEach(({ inputName, errorString }) => {
          if (inputName) {
            inputs[inputName].error = errorString;
          }
        });
        submitFailed();
      }
    }
  };

  const initializeInput = (
    inputName: string, inputRequired: boolean, initialValue: string | SelectionValue = ""
  ): void => {
    setInputs((prevState) => ({
      ...prevState,
      ...{ [inputName]: {
        value: initialValue,
        error: (inputRequired === true || inputRequired === undefined)
          ? bfTranslate('Input required')
          : '',
      } }
    }));
  };

  const addCustomHooks = (kid: JSX.Element): JSX.Element => {
    const inputAttributes = kid.props && kid.props.attributes;
    const inputName = inputAttributes && inputAttributes.name;
    const inputRequired = kid.props && kid.props.validationRules && kid.props.validationRules.isRequired;
    const initialValue = (inputAttributes && inputAttributes.value) || (kid.props && kid.props.selectValue);
    const key = (inputName || ''); // TODO unique key

    // initialize empty input in custom hook's state, default 'isRequired' = true if not specified
    if (inputs && inputName && !inputs[inputName]) initializeInput(inputName, inputRequired, initialValue);

    // inject form info into input React component props
    return React.cloneElement(kid, {
      key,
      attributes: {
        ...inputAttributes,
        ...!inputAttributes.onBlur && { onBlur: handleBlur },
        ...!inputAttributes.onChange && { onChange: handleChange },
        ...!inputAttributes.onFocus && { onFocus: handleFocus },
      },
      input: (inputs && inputName && inputs[inputName]) || {},
      submitAttempted,
    });
  };

  const updateChildren = (kids: JSX.Element[]): JSX.Element[] => (
    React.Children.map(kids, (kidNode) => (kidNode && manageElement(kidNode))) // eslint-disable-line @typescript-eslint/no-use-before-define
  );

  const manageElement = (childNode: JSX.Element): JSX.Element => {
    // add hooks to react components
    if (typeof childNode.type === 'function') {
      return addCustomHooks(childNode);
    }

    if (childNode.props && childNode.props.children) {
      return React.cloneElement(childNode, [], updateChildren(childNode.props.children));
    }

    // return non-react components (e.g. <div> container)
    return childNode;
  };

  React.useEffect(() => {
    // enable submit button until a submission attempt (even if inputs are invalid)
    setSubmitDisabled(submitAttempted ? !isFormValid() : false);
  }, [inputs, isFormValid, submitAttempted]);

  return (
    <form
      className="bf-form"
      noValidate
      onSubmit={handleSubmit}
    >
      {updateChildren(children)}
      <PrimaryButton
        className="bf-form-button"
        disabled={submitDisabled || disabledByParent}
        isLoading={isValidating}
        loadingCopy={bfTranslate("Validating")}
        size={onAsyncCustomValidation ? "medium" : "small"}
        type="submit"
      >
        {submitButtonText ? bfTranslate(submitButtonText) : defaultSubmitButtonText}
      </PrimaryButton>
    </form>
  );
};

export default Form;
